import * as React from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router";
import Costs from "../../components/Details/OverviewInfo/Costs/Costs";
import DeleteModal from "../../components/Forms/Delete/DeleteModal";
import { StatusLabel } from "../../components/Lists/Base/CompleteList/styles";
import { DetailLayoutWrapper } from "../../components/sharedStyles";
import {
  buildQueryString,
  setActiveFormInstance,
  useAllPermissionCheck,
  useFrequentPermissions,
  usePermissionCheck,
} from "../../store/base";
import { useInvoicingErrorPaginationCount } from "../../store/invoicingErrors/hooks/retrieve";
import { constants as editableDocConstants } from "../../store/editabledocs";
import {
  constants as invoicingConstants,
  useParkingInvoicing,
} from "../../store/invoicingParking";
import DetailPageHeaderMenu from "../Layouts/DetailPageHeaderMenu/DetailPageHeaderMenu";
import OverlaySpinner from "../../components/Loaders/OverlaySpinner";
import { useDispatch } from "react-redux";
import { ESignOrManual } from "../../components/Forms/Pickers";
import { useCanUseScrive } from "../../store/editabledocs/hooks/retrieve";
import {
  useParkingContract,
  cancelUrl,
  constants,
} from "../../store/parkingContracts";
import ParkingContractDocumentTabs from "./detail/Documents";
import ParkingContractOverview from "./detail/Overview";
import usePermissionRedirect from "../../hooks/usePermissionRedirect";
import ParkingIMD from "./detail/IMD";
import { toMoneyString } from "../../components/utils/stringUtils";

import ContractInvoicingBase from "../LeaseContract/Detail/ContractInvoicingBase";
import { useFilteredParkingSpots } from "../../store/parkingSpots";
import {
  contractOverviewCosts,
  getContractFromAndTo,
} from "../../components/Lists/utils/costCalculations";

export default function ParkingContractDetail() {
  const storeName = constants.STORE_NAME;
  const dispatch = useDispatch();
  const { path, url } = useRouteMatch();
  const { push } = useHistory();
  const { parkingContractId } = useParams();

  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [cancelTypeOpen, setCancelTypeOpen] = React.useState(false);
  const [parkingContract, parkingContractLoading] =
    useParkingContract(parkingContractId);
  const [invoicingObj] = useParkingInvoicing(
    parkingContract?.parking_invoicing?.id
  );
  const { hasBillectaViewPermission } = useFrequentPermissions();
  usePermissionRedirect(["view_can_contract"]);

  const canDelete = usePermissionCheck("delete_can_contract");
  const canEdit = usePermissionCheck("change_can_contract");
  const [canUseScrive] = useCanUseScrive();
  const canImd = useAllPermissionCheck(["allow_imd", "view_can_imd"]);

  const parkingSpotQuery = buildQueryString({
    id__in: parkingContract?.parking_spots?.map((ap) => ap.id) || [],
  });
  const [parkingSpots] = useFilteredParkingSpots(parkingSpotQuery);

  const combinedCosts = [
    ...(invoicingObj?.cost_set || []),
    ...(parkingSpots?.map((p) => p.cost_set)?.flat() || []),
  ];

  const isCancelled =
    parkingContract?.closed_date || parkingContract?.closed_renew_date;
  const isSigned = parkingContract?.is_signed;

  // clear form states
  React.useEffect(() => {
    return () => {
      dispatch(
        setActiveFormInstance({
          storeName,
          data: {},
        })
      );
      dispatch(
        setActiveFormInstance({
          storeName: invoicingConstants.STORE_NAME,
          data: {},
        })
      );
      dispatch(
        setActiveFormInstance({
          storeName: editableDocConstants.STORE_NAME,
          data: {},
        })
      );
    };
  }, []);

  const [invoicingErrorCount] = useInvoicingErrorPaginationCount({
    filters: {
      parkinginvoicing: parkingContract?.parking_invoicing?.id || -1,
    },
  });

  const SUB_PAGES = [
    {
      path: "/overview",
      label: "Avtal",
      component: ParkingContractOverview,
    },
    {
      path: "/costs",
      label: "Debiteringsrader",
      component: Costs,
    },
    {
      path: "/docs",
      label: "Dokument",
      component: ParkingContractDocumentTabs,
    },
  ];

  if (hasBillectaViewPermission) {
    SUB_PAGES.push({
      path: "/invoicing",
      label: "Avisering",
      hasError: invoicingErrorCount,
      component: ContractInvoicingBase,
    });
  }

  if (canImd) {
    SUB_PAGES.push({
      path: "/imd",
      label: "IMD",
      component: ParkingIMD,
    });
  }

  const subPageLinks = SUB_PAGES.map((sp) => ({
    url: `${url}${sp.path}`,
    label: sp.label,
    hasError: sp.hasError,
  }));

  const handleCancelClicked = () => {
    if (canUseScrive) {
      setCancelTypeOpen(true);
    } else {
      push(cancelUrl({ id: parkingContractId, isManualSign: true }));
    }
  };

  const onCancelTypeSelected = (cancelType) => {
    push(
      cancelUrl({
        id: parkingContractId,
        isManualSign: cancelType !== "digital",
      })
    );
  };

  const handleActions = [];

  if (canEdit && !isCancelled && isSigned) {
    handleActions.push({
      name: "Säg upp",
      onClick: handleCancelClicked,
    });
  }

  if (canDelete) {
    handleActions.push({
      name: "Radera",
      onClick: () => setDeleteModalOpen(true),
      isDelete: true,
    });
  }

  const renderInfoPills = () => {
    if (!parkingContract) return null;
    const { contractFrom, contractTo } = getContractFromAndTo(parkingContract);

    return (
      <>
        <StatusLabel state={parkingContract?.state}>
          {parkingContract?.state_display}
        </StatusLabel>
        {parkingContract?.is_signed && (
          <StatusLabel style={{ marginLeft: 12 }} state={1}>
            Signerat
          </StatusLabel>
        )}
        <StatusLabel style={{ marginLeft: 12 }} state={4}>
          Årsintäkt ({contractFrom.format("YYYY-MM")} -{" "}
          {contractTo.format("YYYY-MM")}):{" "}
          {toMoneyString(
            contractOverviewCosts({
              costsData: combinedCosts,

              contract: parkingContract,
            }),
            true
          )}
        </StatusLabel>
      </>
    );
  };

  return (
    <>
      <DeleteModal
        isOpen={deleteModalOpen}
        closeFunction={() => setDeleteModalOpen(false)}
        constants={constants}
        instance={parkingContract}
        deletedCallback={() => push("/parking-contracts")}
      />

      <ESignOrManual
        isOpen={cancelTypeOpen}
        onClose={() => setCancelTypeOpen(false)}
        onSelect={onCancelTypeSelected}
        description="Bekräfta uppsägning"
        digitalTitle="Signera digitalt"
        manualTitle="Bekräfta manuellt"
        infoText="Välj om uppsägningen ska signeras digitalt eller enbart bekräftas"
      />

      <DetailLayoutWrapper>
        {parkingContractLoading && <OverlaySpinner />}
        <DetailPageHeaderMenu
          title={`Parkeringsavtal ${parkingContract?.id_number || "Laddar..."}`}
          breadCrumbs={[
            { url: "/parking-contracts", label: "Parkeringsavtal" },
            { label: parkingContract?.id_number || "Laddar..." },
          ]}
          eventContentType="standard.parkingcontract"
          eventObjectId={parkingContractId}
          eventIdQueryStr="parkingcontract"
          {...{ subPages: subPageLinks, handleActions, renderInfoPills }}
        />

        <Switch>
          {SUB_PAGES.map((sp) => (
            <Route
              key={sp.path}
              path={`${path}${sp.path}`}
              component={sp.component}
            />
          ))}

          {/* Default sub page  */}
          <Redirect exact to={`${url}${SUB_PAGES[0].path}`} path={`${path}`} />
        </Switch>
      </DetailLayoutWrapper>
    </>
  );
}
